import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import NewsletterForm from '@/components/base/NewsletterForm';
import { articles, categories } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const fallbackArticle = articles[0];
  const article = articles.find((a) => a.slug === slug) || fallbackArticle;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <p className="text-foreground-500">文章不存在</p>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '吃瓜官网',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <article className="mx-auto px-4 md:px-6 max-w-4xl">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '分类', href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <header className="mb-8">
              <div className="flex items-center flex-wrap gap-2 mb-3">
                {article.isHot && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium">
                    <i className="ri-fire-line"></i> 热门
                  </span>
                )}
                {article.isNew && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                    <i className="ri-sparkling-line"></i> 最新
                  </span>
                )}
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    {category.name}
                  </Link>
                )}
              </div>

              <h1 className="font-heading font-black text-xl md:text-3xl text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center flex-wrap gap-3 text-foreground-500 text-sm">
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line"></i>
                  </div>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line"></i>
                  </div>
                  {article.publishedAt}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line"></i>
                  </div>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </div>
                  {(article.viewCount / 10000).toFixed(0)}万阅读
                </span>
              </div>
            </header>

            <figure className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 吃瓜官网独家爆料`}
                className="w-full h-auto object-cover object-top aspect-video"
              />
            </figure>

            <div
              className="prose prose-sm md:prose-base max-w-none mb-10 text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap items-center gap-2 mb-10 pt-6 border-t border-background-200/70">
              <span className="text-sm text-foreground-500 font-medium">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="mx-auto px-4 md:px-6 max-w-4xl pb-10">
              <h3 className="font-heading font-bold text-lg text-foreground-900 mb-4">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} />
                ))}
              </div>
            </section>
          )}

          <NewsletterForm />
        </main>

        <Footer />
      </div>
    </>
  );
}